// ============================================================================
// Beacon — usage metering. Every billable provider call (Claude AI, ATTOM,
// RentCast, Regrid, Resend, GHL, government feeds) logs an event with the actor
// and an estimated cost, so the owner's Admin board can see all usage per user.
// Cloud `usage_events` with a graceful local fallback. Costs are estimates for
// visibility — reconcile against provider invoices for exact billing.
// ============================================================================

import { getSupabase, isCloud } from "./supabase";

export type Provider =
  | "AI (Claude)" | "ATTOM" | "RentCast" | "Regrid" | "HUD FMR" | "Census" | "FEMA" | "Email (Resend)" | "GoHighLevel";

export interface UsageEvent {
  id: string;
  provider: Provider;
  action: string; // e.g. "deal analysis", "offer letter", "enrich", "email"
  actor: string; // who triggered it (email in cloud, else "local")
  units: number; // calls (or tokens where known)
  cost: number; // estimated $
  created_at: string;
}

/** Rough per-call cost estimates ($) — for visibility, not invoicing. */
export const EST_COST: Record<Provider, number> = {
  "AI (Claude)": 0.09, ATTOM: 0.25, RentCast: 0.05, Regrid: 0.03,
  "HUD FMR": 0, Census: 0, FEMA: 0, "Email (Resend)": 0.001, GoHighLevel: 0,
};

let counter = 0;
const newId = () => { counter += 1; return "ue" + Date.now().toString(36) + counter.toString(36); };

const LS = "beacon_usage";
function localAll(): UsageEvent[] { try { return JSON.parse(localStorage.getItem(LS) || "[]"); } catch { return []; } }
function localSave(a: UsageEvent[]) { try { localStorage.setItem(LS, JSON.stringify(a.slice(-2000))); } catch { /* */ } }

async function actor(): Promise<string> {
  if (isCloud()) {
    try { const { data } = await getSupabase()!.auth.getSession(); if (data.session?.user?.email) return data.session.user.email; } catch { /* */ }
  }
  return "local";
}

/** Record one billable event. Best-effort; never throws into the caller. */
export async function logUsage(provider: Provider, action: string, opts?: { units?: number; cost?: number }): Promise<void> {
  try {
    const units = opts?.units ?? 1;
    const cost = opts?.cost ?? EST_COST[provider] * units;
    const ev: UsageEvent = { id: newId(), provider, action, actor: await actor(), units, cost, created_at: new Date().toISOString() };
    if (isCloud()) {
      try { const { error } = await getSupabase()!.from("usage_events").insert(ev); if (!error) return; } catch { /* fall through */ }
    }
    const all = localAll(); all.push(ev); localSave(all);
  } catch { /* metering must never break a feature */ }
}

export async function listUsage(): Promise<UsageEvent[]> {
  if (isCloud()) {
    try {
      const { data, error } = await getSupabase()!.from("usage_events").select("*").order("created_at", { ascending: false }).limit(2000);
      if (error) throw error;
      if (data) return data as UsageEvent[];
    } catch { /* fall through */ }
  }
  return localAll().slice().reverse();
}

export interface UsageSummary {
  total: number; count: number;
  byProvider: { provider: Provider; count: number; units: number; cost: number }[];
  byActor: { actor: string; count: number; cost: number }[];
}

/** Aggregate events (optionally within the last `sinceDays`). */
export function summarize(events: UsageEvent[], sinceDays?: number): UsageSummary {
  const cutoff = sinceDays ? Date.now() - sinceDays * 864e5 : 0;
  const evs = events.filter((e) => !cutoff || new Date(e.created_at).getTime() >= cutoff);
  const pMap = new Map<Provider, { count: number; units: number; cost: number }>();
  const aMap = new Map<string, { count: number; cost: number }>();
  let total = 0;
  for (const e of evs) {
    total += e.cost || 0;
    const p = pMap.get(e.provider) || { count: 0, units: 0, cost: 0 };
    p.count++; p.units += e.units || 1; p.cost += e.cost || 0; pMap.set(e.provider, p);
    const a = aMap.get(e.actor) || { count: 0, cost: 0 };
    a.count++; a.cost += e.cost || 0; aMap.set(e.actor, a);
  }
  return {
    total, count: evs.length,
    byProvider: [...pMap.entries()].map(([provider, v]) => ({ provider, ...v })).sort((x, y) => y.cost - x.cost),
    byActor: [...aMap.entries()].map(([actor, v]) => ({ actor, ...v })).sort((x, y) => y.cost - x.cost),
  };
}
